import { Link } from 'react-router';
import { motion } from 'motion/react';
import { Button } from '@mieweb/ui';
import type { CompetitorPricing } from '../../data/competitors';
import { pollenatePricing } from '../../data/competitors';
import { BrandMark } from '../BrandMark';

interface PricingComparisonProps {
  competitorName: string;
  competitorPricing: CompetitorPricing;
  competitorPricingUrl: string;
}

function PricingCard({ plan, brand }: { plan: { name: string; price: string; period?: string; features: string[]; highlighted?: boolean }; brand: 'pollenate' | 'competitor' }) {
  const isPollenate = brand === 'pollenate';
  const isHighlighted = plan.highlighted;

  return (
    <div className={`relative rounded-xl border p-6 ${
      isHighlighted
        ? 'border-amber-300 dark:border-amber-700 bg-amber-50/50 dark:bg-amber-950/20 shadow-lg shadow-amber-100/50 dark:shadow-amber-900/10'
        : 'border-slate-200 dark:border-neutral-800 bg-white dark:bg-neutral-900'
    }`}>
      {isHighlighted && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-amber-500 text-white text-xs font-bold px-3 py-1 rounded-full">
          {isPollenate ? 'Best Value' : 'Most Popular'}
        </span>
      )}
      <h4 className="text-lg font-semibold text-slate-900 dark:text-white mb-1">{plan.name}</h4>
      <div className="flex items-baseline gap-1 mb-4">
        <span className="text-3xl font-bold text-slate-900 dark:text-white">{plan.price}</span>
        {plan.period && <span className="text-sm text-slate-500 dark:text-neutral-400">{plan.period}</span>}
      </div>
      <ul className="space-y-2">
        {plan.features.map((feature) => (
          <li key={feature} className="flex items-start gap-2 text-sm text-slate-600 dark:text-neutral-400">
            <svg className={`w-4 h-4 mt-0.5 shrink-0 ${isPollenate ? 'text-amber-500' : 'text-slate-400 dark:text-neutral-500'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {feature}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function PricingComparison({ competitorName, competitorPricing, competitorPricingUrl }: PricingComparisonProps) {
  return (
    <section className="py-20 px-4 sm:px-6 lg:px-8 bg-slate-50/50 dark:bg-neutral-950/50">
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 dark:text-white mb-4">Pricing Comparison</h2>
          <p className="text-lg text-slate-600 dark:text-neutral-400">
            See how Pollenate&apos;s transparent pricing compares to {competitorName}.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-10">
          {/* Pollenate */}
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <div className="flex items-center gap-2 mb-2">
              <BrandMark className="h-7 w-7" />
              <h3 className="text-xl font-bold text-amber-600 dark:text-amber-400">Pollenate</h3>
            </div>
            <p className="text-sm text-slate-500 dark:text-neutral-400 mb-6">{pollenatePricing.model}</p>
            <div className="space-y-4">
              {pollenatePricing.plans.map((plan) => (
                <PricingCard key={plan.name} plan={plan} brand="pollenate" />
              ))}
            </div>
          </motion.div>

          {/* Competitor */}
          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="flex items-center gap-2 mb-2">
              <h3 className="text-xl font-bold text-slate-700 dark:text-neutral-300">{competitorName}</h3>
            </div>
            <p className="text-sm text-slate-500 dark:text-neutral-400 mb-6">{competitorPricing.model}</p>
            <div className="space-y-4">
              {competitorPricing.plans.map((plan) => (
                <PricingCard key={plan.name} plan={plan} brand="competitor" />
              ))}
            </div>
          </motion.div>
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center mt-12"
        >
          <Link to="/login?mode=signup">
            <Button size="lg">Start Free — No Credit Card Required</Button>
          </Link>
        </motion.div>

        {/* Pricing disclaimer */}
        <p className="text-center text-xs text-slate-400 dark:text-neutral-500 mt-8 max-w-2xl mx-auto leading-relaxed">
          {competitorName} pricing information sourced from{' '}
          <a href={competitorPricingUrl} target="_blank" rel="noopener noreferrer" className="underline hover:text-slate-600 dark:hover:text-neutral-300 transition-colors">
            {new URL(competitorPricingUrl).hostname}
          </a>{' '}
          as of March 17, 2026. Pricing and features may change. Visit {competitorName}&apos;s website for the most current information.
          Pollenate pricing is accurate as of March 17, 2026 — see our{' '}
          <Link to="/#pricing" className="underline hover:text-slate-600 dark:hover:text-neutral-300 transition-colors">pricing page</Link> for the latest.
        </p>
      </div>
    </section>
  );
}
